import { Fragment, useEffect, useState } from "react"
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router"
import { Capacitor } from "@capacitor/core"
import { PushNotifications } from "@capacitor/push-notifications"
import { ArrowLeft } from "@/components/icons"
import { TopBarBackButton } from "@/components/topbar/TopBarBackButton"
import { AppLayout } from "@/components/AppLayout"
import { DesktopPage } from "@/components/desktop/DesktopPage"
import { DesktopPageHeader } from "@/components/desktop/DesktopPageHeader"
import { useIsDesktop } from "@/components/ui/use-mobile"
import { Card } from "@/components/ui/card"
import { Switch } from "@/components/ui/switch"
import { DetailSkeleton } from "@/components/ui/skeleton-presets"
import {
  useCrewGetNotificationSettings,
  useCrewUpdateNotificationSettings,
} from "@/lib/hooks/crew"

export const Route = createFileRoute("/_auth/profile/settings/notifications")({
  component: RouteComponent,
})

// Keys are the Crew `notify*` columns (preferences live in Postgres).
type PreferenceKey =
  | "notifyAnnouncements"
  | "notifyBirthdays"
  | "notifyChat"
  | "notifyDocumentExpiry"
  | "notifyFlights"
  | "notifyMaintenance"
  | "notifyPaxUpdates"
  | "notifyRoster"
  | "notifyTrips"

type Preferences = Record<PreferenceKey, boolean>

// Order + labels follow the "Notify me about" design.
const CATEGORIES: { key: PreferenceKey; label: string }[] = [
  { key: "notifyAnnouncements", label: "Announcements" },
  { key: "notifyBirthdays", label: "Birthdays" },
  { key: "notifyChat", label: "Chat" },
  { key: "notifyDocumentExpiry", label: "Document Expiry" },
  { key: "notifyFlights", label: "Flight Schedules" },
  { key: "notifyMaintenance", label: "Maintenance" },
  { key: "notifyPaxUpdates", label: "PAX Updates" },
  { key: "notifyRoster", label: "Schedule" },
  { key: "notifyTrips", label: "Trips" },
]

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (value: boolean) => void
}) {
  return (
    <div className="flex min-h-[44px] items-center justify-between px-2 py-2">
      <span className="text-sm text-content-primary-bold">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  )
}

function RouteComponent() {
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()

  const { data: serverPrefs, isLoading } = useCrewGetNotificationSettings()
  const { mutate: updatePreferences } = useCrewUpdateNotificationSettings()

  // Mirror server prefs locally so toggles feel instant; re-sync when the
  // query updates.
  const [prefs, setPrefs] = useState<Preferences | null>(null)
  useEffect(() => {
    if (serverPrefs) setPrefs(serverPrefs as Preferences)
  }, [serverPrefs])

  // Surface when the device push permission is off — in-app alerts still work,
  // but no banners until the user re-enables them in Settings.
  const [permissionDenied, setPermissionDenied] = useState(false)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    void PushNotifications.checkPermissions().then((status) =>
      setPermissionDenied(status.receive === "denied")
    )
  }, [])

  const allOn = prefs ? CATEGORIES.every((c) => prefs[c.key]) : false

  function applyChange(changes: Partial<Preferences>) {
    if (!prefs) return
    setPrefs({ ...prefs, ...changes })
    updatePreferences(changes)
  }

  const body = (
    <>
      {isLoading || !prefs ? (
        <DetailSkeleton rows={10} />
      ) : (
        <div className="flex flex-col gap-4">
          {permissionDenied && (
            <p className="px-1 text-xs text-content-primary-subtle">
              Push notifications are turned off for Pronteer in your device Settings.
              In-app alerts still appear, but you won't get banners until you
              re-enable them.
            </p>
          )}
          <div>
            <p className="mb-4 px-1 text-sm font-semibold text-content-primary-bold">
              Notify me about
            </p>
            <Card className="flex w-full flex-col px-2">
              <ToggleRow
                label="All"
                checked={allOn}
                onChange={(value) =>
                  applyChange(
                    Object.fromEntries(
                      CATEGORIES.map((c) => [c.key, value])
                    ) as Partial<Preferences>
                  )
                }
              />
              <hr className="border-border-L1" />
              {CATEGORIES.map((category, index) => (
                <Fragment key={category.key}>
                  {index > 0 && <hr className="border-border-L1" />}
                  <ToggleRow
                    label={category.label}
                    checked={prefs[category.key]}
                    onChange={(value) => applyChange({ [category.key]: value })}
                  />
                </Fragment>
              ))}
            </Card>
          </div>
        </div>
      )}
    </>
  )

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader
          title="Notifications"
          breadcrumb={
            <span className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate({ to: "/profile/settings" })}
                className="flex size-7 items-center justify-center rounded-full hover:bg-surface-L1"
                aria-label="Back to settings"
              >
                <ArrowLeft className="size-4" />
              </button>
              <span>
                <Link to="/profile" className="hover:underline">
                  Profile
                </Link>
                <span className="text-content-primary-subtle"> / </span>
                <Link to="/profile/settings" className="hover:underline">
                  Settings
                </Link>
                <span className="text-content-primary-subtle">
                  {" "}
                  / Notifications
                </span>
              </span>
            </span>
          }
        />
        {body}
      </DesktopPage>
    )
  }

  return (
    <AppLayout topBar={<TopBarBackButton title="Notifications" />}>
      {body}
    </AppLayout>
  )
}
